import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Button, Spinner } from '@w7/ui'
import { useAggregateMe } from '@/api/hooks/useAggregateMe'
import {
  clearAllAuth,
  isAccessTokenExpired,
  isDemoSession,
  logout,
  refreshAccessToken,
  rememberNext,
  resolveAuthPhase,
  type AuthPhase,
} from '@/auth/oidc'
import { startSessionWatch } from '@/auth/sessionWatch'
import { flashToast, toastCopy } from '@/ui/toaster'

/**
 * Gates the authenticated shell (CM AuthGate pattern).
 * Public routes (/login, /callback, /no-access) render outside this component.
 *
 * Live: token → POST /v1/me (Aggregate-API attaches aggregate_members).
 * Demo: skip /v1/me (fixture console).
 */
export function AuthGate({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const [phase, setPhase] = useState<AuthPhase>(() => resolveAuthPhase())
  const demo = isDemoSession()
  const me = useAggregateMe()

  useEffect(() => {
    setPhase(resolveAuthPhase())
  }, [location.pathname, location.search])

  useEffect(() => {
    if (phase.phase !== 'expired') return
    let cancelled = false
    ;(async () => {
      const ok = await refreshAccessToken()
      if (cancelled) return
      if (ok && !isAccessTokenExpired()) {
        setPhase(resolveAuthPhase())
        return
      }
      clearAllAuth()
      flashToast(toastCopy.loginExpired)
      setPhase({ phase: 'anon' })
    })()
    return () => {
      cancelled = true
    }
  }, [phase.phase])

  useEffect(() => {
    if (phase.phase !== 'ready' || demo) return
    if (!me.isError) return
    clearAllAuth()
  }, [phase.phase, demo, me.isError])

  useEffect(() => {
    if (phase.phase !== 'ready') return
    if (!demo && (me.isLoading || me.isError || !me.data?.ok)) return
    return startSessionWatch()
  }, [phase.phase, demo, me.isLoading, me.isError, me.data?.ok])

  if (phase.phase === 'loading') {
    return (
      <div className="w7-auth w7-auth-center" data-testid="auth-loading">
        <Spinner size={22} />
        <p className="w7-dim" style={{ marginTop: 12 }}>
          Checking session…
        </p>
      </div>
    )
  }

  if (phase.phase === 'anon' || (phase.phase === 'ready' && me.isError && !demo)) {
    rememberNext(`${location.pathname}${location.search}`)
    const reason = me.isError ? 'denied' : undefined
    const q = new URLSearchParams()
    q.set('next', location.pathname + location.search)
    if (reason) q.set('reason', reason)
    return <Navigate to={`/login?${q.toString()}`} replace />
  }

  if (phase.phase === 'expired') {
    return (
      <div className="w7-auth w7-auth-center" data-testid="auth-refreshing">
        <Spinner size={22} />
        <p className="w7-dim" style={{ marginTop: 12 }}>
          Refreshing session…
        </p>
      </div>
    )
  }

  if (phase.phase === 'no-access') {
    return <Navigate to="/no-access" replace />
  }

  if (phase.phase === 'ready' && !demo && me.isLoading) {
    return (
      <div className="w7-auth w7-auth-center" data-testid="auth-me-check">
        <Spinner size={22} />
        <p className="w7-dim" style={{ marginTop: 12 }}>
          Checking Aggregate access…
        </p>
      </div>
    )
  }

  if (phase.phase === 'ready' && !demo && me.data?.ok) {
    if (!me.data.caller.aggregateGroupId || me.data.caller.accessLevel !== 'Aggregate') {
      return <Navigate to="/no-access" replace />
    }
    return <>{children}</>
  }

  if (phase.phase === 'ready' && demo) {
    return <>{children}</>
  }

  return (
    <div className="w7-auth w7-auth-center">
      <p>Unexpected auth state.</p>
      <Button onClick={() => logout()}>Sign out</Button>
    </div>
  )
}
